/**
 * Scene Setter — The Courtyard
 *
 * An immersive introduction to the three Greek characters who guide
 * the Finding Your Voice experience: Philemon, Xenia, and Theron.
 */

export const SCENE_TITLE = 'The Courtyard';

export const SCENE_SECTIONS = [
  {
    id: 'opening',
    title: 'Drawing You In',
    character: null,
    type: 'narration',
    content: [
      'You hear about Xenia\'s courtyard the way most people do: someone mentions it in passing, almost as an afterthought. A place where people gather in the evenings. Where conversations go somewhere.',
      'You find the gate on a narrow street off the agora, half-hidden behind a trailing vine of jasmine. You push it open.',
      'The courtyard is smaller than you expected. Warmer, too. Oil lamps flicker on low stone walls, casting soft orange light across worn paving stones. Herbs grow between the cracks. You catch the scent of rosemary as you step inside.',
      'Three people sit around a low table. One is mid-story, gesturing broadly. Another listens with quiet attention, fingers resting on a clay cup. The third notices you at the gate before the others do.',
    ],
  },
  {
    id: 'meetingTheron',
    title: 'Meeting Theron',
    character: 'Theron',
    type: 'mixed',
    content: [
      'The one telling the story has a weathered face and hands that move constantly, shaping the air as if his words alone aren\'t quite enough. This is Theron. A merchant by trade, a storyteller by nature, and a man who has never met a silence he didn\'t feel personally responsible for filling.',
      'He\'s recounting an encounter with a Carthaginian grain trader (an enormous man, arms like ship masts) who stopped him mid-negotiation, looked him up and down, and said: "You talk too much."',
      'Theron grins as he delivers his own reply.',
      'Theron: "Yes. But have you noticed you\'re still listening?"',
      'Philemon: "That could have gone very badly for you."',
      'Theron: "It nearly did! He stared at me for what felt like an hour. I thought, well, Theron, you\'ve finally talked yourself into an early grave. And then... he laughed. Bought twice what he\'d planned. Invited me to dinner. Tried to marry me to his daughter."',
      'Xenia: "Did you accept?"',
      'Theron: "She was lovely, but she lived in Carthage. Too far from good wine."',
    ],
  },
  {
    id: 'meetingPhilemon',
    title: 'Meeting Philemon',
    character: 'Philemon',
    type: 'mixed',
    content: [
      'The quiet man across the table has kind eyes and ink-stained fingers. He offers you a small, warm smile, the sort that doesn\'t demand anything in return. This is Philemon. A teacher of rhetoric who, you will come to learn, is far more interested in listening than lecturing.',
      'You don\'t notice him at first. That\'s part of what makes him remarkable. When he does speak, something shifts: you lean in without meaning to, because everything he says makes you feel that what you\'re saying matters.',
      'Philemon: "Theron believes every problem can be solved by talking. Preferably at length."',
      'Theron: "It\'s worked so far."',
      'Philemon: "It nearly got you thrown off a dock in Syracuse."',
      'Theron: "That was a misunderstanding."',
      'Philemon: "You insulted the man\'s boat."',
      'Theron: "I observed that it was small. There\'s a difference."',
      'Xenia wipes her eyes, laughing, and reaches for an olive.',
      'Xenia: "In fairness, Philemon, you once forgot how to speak entirely in front of a room of your own students. For a very long time. I heard it described as \'painfully eternal.\'"',
      'Philemon: "Thank you for that reminder."',
      'Xenia: "I bring it up with love."',
      'Philemon: "You bring it up constantly."',
      'Xenia: "Also with love."',
    ],
  },
  {
    id: 'meetingXenia',
    title: 'Meeting Xenia',
    character: 'Xenia',
    type: 'mixed',
    content: [
      'Xenia is harder to describe. It is less what she says than something in how she holds space. Her face carries lines from years of smiling at other people\'s stories. When she looks at you, it doesn\'t feel like being watched. It feels like being seen.',
      'She\'s the one who noticed you at the gate. She doesn\'t call out or wave. She simply makes room.',
      'Theron waves you over with a broad hand.',
      'Theron: "Sit. You look like someone who\'s been standing too long."',
      'Philemon nods gently, a quiet welcome that somehow says more than Theron\'s entire greeting.',
    ],
  },
  {
    id: 'conversationTurns',
    title: 'The Conversation Turns',
    character: null,
    type: 'mixed',
    content: [
      'Xenia pours you a cup without asking. The wine is rough but honest, like the courtyard itself.',
      'They\'re discussing someone named Demetrios, who spoke at a gathering the previous evening.',
      'Theron: "It was a disaster. Magnificent. His voice cracked on his own name."',
      'Xenia: "Theron."',
      'Theron: "What? I\'m not mocking the boy. I\'m admiring him. Because well into it, he was dying up there. Forgetting his points. Apologising to the air. And then he stopped trying to remember his speech and just... talked. About what he actually believed. And the room shifted. Even old Aristocles was awake, and Aristocles sleeps through everything."',
      'Philemon: "The room shifted. I felt it."',
      'Xenia sets down her cup.',
      'Xenia: "He stopped performing. He started speaking."',
    ],
  },
  {
    id: 'heartOfIt',
    title: 'The Heart of It',
    character: 'Philemon',
    type: 'mixed',
    content: [
      'Philemon goes quiet for a moment. Not the comfortable quiet of someone gathering thoughts, but the deeper quiet of someone deciding whether to share something real.',
      'Philemon: "My first year at the Lyceum, I spent most of it in silence. Not by choice. My throat would close before I could speak. My thoughts would scatter like startled birds."',
      'He turns the cup slowly in his hands.',
      'Philemon: "My teacher, Eudoros, told me something I\'ve never forgotten. He said: \'You believe speaking is about being impressive. It is not. It is about offering what is true for you. The offering is enough. The trembling can come too.\'"',
      'He looks up.',
      'Philemon: "The trembling still comes. But it doesn\'t get to decide anymore."',
    ],
  },
  {
    id: 'xeniasTruth',
    title: 'Xenia\'s Truth',
    character: 'Xenia',
    type: 'mixed',
    content: [
      'Xenia: "My path was different. I never feared speaking. I feared not being heard."',
      'She says this without bitterness, but the weight of it settles over the table.',
      'Xenia: "In Athens, the rooms where intellectual life happened, the symposia, the academies, those rooms weren\'t built for women. The doors weren\'t locked, but they weren\'t exactly open either."',
      'She gestures at the courtyard around you: the lamps, the herbs, the low table.',
      'Xenia: "So I built my own room."',
      'A pause.',
      'Xenia: "Finding your voice isn\'t just about courage. Sometimes it\'s about finding, or building, a room that\'s willing to hear you."',
    ],
  },
  {
    id: 'invitation',
    title: 'The Invitation',
    character: null,
    type: 'mixed',
    content: [
      'The evening has deepened. The lamps burn lower. The noise of the city beyond the walls has faded to a distant murmur.',
      'Theron leans forward.',
      'Theron: "So. What brings you to Xenia\'s courtyard?"',
      'Philemon, ever the reframer, offers a gentler version of the same question.',
      'Philemon: "What\'s your relationship with your own voice?"',
      'Xenia smiles.',
      'Xenia: "We don\'t have the answers. I want to be honest about that from the start. But between us, we\'ve made most of the mistakes worth making."',
      'She catches your eye.',
      'Xenia: "Take your time. We\'re not going anywhere."',
    ],
  },
  {
    id: 'transitionToAssessment',
    title: null,
    character: null,
    type: 'narration',
    content: [
      '*The questions that follow aren\'t a test. They\'re the beginning of a conversation, one that will shape the guidance Philemon, Xenia, and Theron offer you as you find your voice.*',
      '*Philemon will ask about what you fear. Xenia will ask about where you feel connected. Theron will ask about what you need to get done.*',
      '*There are no wrong answers. Only honest ones.*',
    ],
  },
];
